/**
 * Canonical slug + URL helpers.
 *
 * Canonical routes:
 *   entity events -> /<entity-type>/<entity-slug>/events   e.g. /partner/elastic/events
 *   event detail  -> /event/<numeric-id>/<event-slug>      e.g. /event/66483/elasticsearch-engineer
 *
 * This mirrors `src/utils/slug.js` on the backend. The two implementations must
 * stay in sync or canonical redirects will ping-pong between client and server.
 */

const TRANSLITERATIONS: Record<string, string> = {
  æ: 'ae', ø: 'o', å: 'a', ð: 'd', þ: 'th', ß: 'ss',
  œ: 'oe', ł: 'l', đ: 'd', ħ: 'h', ŋ: 'n', ŧ: 't',
};

/** Convert arbitrary text to a lowercase, hyphen-separated URL slug. */
export function toSlug(value: unknown): string {
  if (value == null) return '';
  let text = String(value).toLowerCase().trim();
  if (!text) return '';

  text = text.replace(/[æøåðþßœłđħŋŧ]/g, (ch) => TRANSLITERATIONS[ch] || ch);
  text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  text = text.replace(/&/g, ' and ');
  text = text.replace(/[^a-z0-9]+/g, '-');
  text = text.replace(/^-+|-+$/g, '');
  return text;
}

/** Lowercase singular entity-type segment, e.g. 'Partner' -> 'partner'. */
export function toEntityTypeSlug(typeName: unknown): string {
  return toSlug(typeName);
}

export interface EntitySlugSource {
  entityId?: number | null;
  entityName?: string | null;
  searchFriendlyName?: string | null;
  entityType?: string | null;
  slug?: string | null;
  entityTypeSlug?: string | null;
}

/**
 * Slug for an entity. `searchFriendlyName` wins when non-blank so that an
 * import artefact like "Elastic Americas" resolves to /partner/elastic.
 */
export function buildEntitySlug(entity: EntitySlugSource | null | undefined): string {
  if (!entity) return '';
  if (entity.slug) return entity.slug;
  const friendly = toSlug(entity.searchFriendlyName);
  if (friendly) return friendly;
  const name = toSlug(entity.entityName);
  if (name) return name;
  return entity.entityId != null ? `entity-${entity.entityId}` : '';
}

/** Canonical path for an entity's event list. '' when the entity is unusable. */
export function buildEntityEventsPath(entity: EntitySlugSource | null | undefined): string {
  if (!entity) return '';
  const type = entity.entityTypeSlug || toEntityTypeSlug(entity.entityType);
  const slug = buildEntitySlug(entity);
  if (!type || !slug) return '';
  return `/${type}/${slug}/events`;
}

export interface EventSlugSource {
  eventId?: number | string | null;
  databaseEventId?: number | string | null;
  title?: string | null;
}

/** Canonical path for an event detail page. '' when the id is not numeric. */
export function buildEventPath(event: EventSlugSource | null | undefined): string {
  if (!event) return '';
  const id = Number(event.eventId ?? event.databaseEventId);
  if (!Number.isInteger(id) || id <= 0) return '';
  const slug = toSlug(event.title);
  return slug ? `/event/${id}/${slug}` : `/event/${id}`;
}

/** Absolute canonical URL, used by exports, ICS files, and structured data. */
export function buildEventUrl(
  event: EventSlugSource | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const path = buildEventPath(event);
  if (!path) return '';
  const base = (origin || '').replace(/\/+$/, '');
  return base ? `${base}${path}` : path;
}

/** Absolute canonical URL for an entity's event list. */
export function buildEntityEventsUrl(
  entity: EntitySlugSource | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const path = buildEntityEventsPath(entity);
  if (!path) return '';
  const base = (origin || '').replace(/\/+$/, '');
  return base ? `${base}${path}` : path;
}

/** Slug-safe filename stem, e.g. "elastic-events-2026-08-15". */
export function buildExportFilename(label: string, extension: string, today: Date = new Date()): string {
  const stem = toSlug(label) || 'events';
  const date = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
  return `${stem}-events-${date}.${extension}`;
}
